// Features: M004-F0778
// Tests: T-M004-0386
// WebUI #250 (room-controls-relay 3.0.0, Core #531): the room-controls panel. Shows only what describe returns: room state,
// room temperature and target, and sensors. Core is the authority on rights and binding; the UI holds no permission key.
// Visibility, outcome wording and the Idempotency-Key rules come from roomControlsModel (design M004-F0778 sections 2 and 4).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import roomControlsApi from '../../services/roomControlsApi';
import {
  stateOptions,
  formatTemperature,
  targetBounds,
  validateTarget,
  describeVisibility,
  setOutcomeView,
  createAttemptStore,
  setBody,
} from './roomControlsModel';
import './RoomControlsPanel.css';

const newKey = () => globalThis.crypto?.randomUUID?.() || `rc-${Date.now()}-${Math.random()}`;

const QUALITY_WORDS = Object.freeze({
  stale: 'Stale',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
  missing: 'Missing',
});

const sensorValueText = (sensor) => (
  sensor.value == null ? 'Not reported' : `${sensor.value}${sensor.unit ? ` ${sensor.unit}` : ''}`
);

// showSensors: the admin Climate tab passes false, because the Sensors tab lists the same describe.sensors read-only.
const RoomControlsPanel = ({ roomId, onHidden, showSensors = true }) => {
  // view.status: 'loading' | 'panel' | 'reason' | 'unavailable' | 'hidden'
  const [view, setView] = useState({ status: 'loading' });
  const [outcomes, setOutcomes] = useState({});
  const [inFlight, setInFlight] = useState(null); // { control, value } while a set is sent
  const [targetInput, setTargetInput] = useState('');
  const [targetError, setTargetError] = useState('');
  const [stale, setStale] = useState(false);

  const attemptsRef = useRef(null);
  if (!attemptsRef.current) attemptsRef.current = createAttemptStore(newKey);
  const attempts = attemptsRef.current;

  const aliveRef = useRef(false);
  const onHiddenRef = useRef(onHidden);
  onHiddenRef.current = onHidden;

  // resetInput: only the first read and Retry set the target box; a re-read after a set leaves what the user typed alone.
  // afterSet: a re-read after a set that does not come back keeps the panel and its outcome text and marks the values as possibly out
  // of date (M004-F0778 §4), so an "Outcome unknown" line is never replaced by a whole-panel "unavailable".
  const load = useCallback(async ({ resetInput = false, afterSet = false } = {}) => {
    let result;
    try {
      result = { ok: true, data: await roomControlsApi.describe(roomId) };
    } catch (cause) {
      result = { ok: false, cause };
    }
    if (!aliveRef.current) return;
    const visibility = describeVisibility(result);
    if (afterSet && visibility.kind !== 'panel') {
      setStale(true);
      return;
    }
    setStale(false);
    if (visibility.kind === 'panel') {
      const target = result.data?.target_temperature;
      if (resetInput) setTargetInput(target?.value != null ? String(target.value) : '');
      setView({ status: 'panel', describe: result.data });
    } else if (visibility.kind === 'reason') {
      setView({ status: 'reason', reason: visibility.reason });
    } else if (visibility.kind === 'unavailable') {
      setView({ status: 'unavailable' });
    } else {
      setView({ status: 'hidden' });
    }
  }, [roomId]);

  useEffect(() => {
    aliveRef.current = true;
    load({ resetInput: true });
    return () => { aliveRef.current = false; };
  }, [load]);

  useEffect(() => {
    if (view.status === 'hidden') onHiddenRef.current?.();
  }, [view.status]);

  const retry = () => {
    setView({ status: 'loading' });
    load({ resetInput: true });
  };

  const send = async (control, value) => {
    setInFlight({ control, value });
    setOutcomes((prev) => ({ ...prev, [control]: null }));
    const key = attempts.keyFor(control, value);
    let outcome;
    try {
      const result = await roomControlsApi.set(roomId, setBody(control, value), key);
      outcome = setOutcomeView({ control, result, value });
    } catch (cause) {
      outcome = setOutcomeView({ control, cause, value });
    }
    attempts.settle(control, outcome.keepKey);
    if (!aliveRef.current) return;
    setOutcomes((prev) => ({ ...prev, [control]: outcome }));
    setInFlight(null);
    // Re-describe after every answer except a request the page refused before sending.
    if (outcome.kind !== 'invalid') await load({ afterSet: true });
  };

  const onSetTarget = () => {
    const text = targetInput.trim();
    const value = text === '' ? NaN : Number(text);
    const message = validateTarget(value, view.describe);
    if (message) {
      setTargetError(message);
      return;
    }
    setTargetError('');
    send('target_temperature', value);
  };

  if (view.status === 'hidden') return null;

  const frame = (children) => (
    <section className="room-controls-panel control-panel" data-testid="room-controls-panel">
      <h3>Room controls</h3>
      {children}
    </section>
  );

  if (view.status === 'loading') {
    return frame(<p className="rc-muted" role="status">Loading room controls…</p>);
  }
  if (view.status === 'reason') {
    return frame(<p className="rc-reason" role="alert" data-testid="room-controls-reason">{view.reason}</p>);
  }
  if (view.status === 'unavailable') {
    return frame(
      <div className="rc-unavailable">
        <p role="status" data-testid="room-controls-unavailable">Room controls are unavailable right now.</p>
        <button type="button" data-testid="room-controls-retry" onClick={retry}>Retry</button>
      </div>,
    );
  }

  const d = view.describe;
  const roomState = d.room_state;
  const target = d.target_temperature;
  const bounds = targetBounds(d);
  const busy = inFlight !== null;
  const stateOutcome = outcomes.room_state;
  const targetOutcome = outcomes.target_temperature;

  return frame(
    <>
      {stale && (
        <p className="rc-muted" role="status" data-testid="room-controls-stale">
          Couldn't refresh the room — what's shown may be out of date.{' '}
          <button type="button" data-testid="room-controls-refresh" onClick={() => load({ afterSet: true })}>Refresh</button>
        </p>
      )}
      {roomState && (
        <div className="rc-section" data-testid="room-controls-state-section">
          <h4>Room state</h4>
          {roomState.value == null && <p className="rc-muted">Not reported</p>}
          <div className="rc-state-buttons">
            {stateOptions(d).map(({ value, label }) => {
              const sending = inFlight?.control === 'room_state' && inFlight.value === value;
              return (
                <button
                  key={value}
                  type="button"
                  data-testid={`room-controls-state-${value}`}
                  aria-pressed={roomState.value === value}
                  disabled={busy}
                  onClick={() => send('room_state', value)}
                >
                  {sending ? 'Sending…' : label}
                </button>
              );
            })}
          </div>
          {stateOutcome && (
            <p role={stateOutcome.role} data-testid="room-controls-state-outcome">{stateOutcome.text}</p>
          )}
        </div>
      )}

      <div className="rc-section" data-testid="room-controls-temperature-section">
        <h4>Temperature</h4>
        <p className="rc-reading">
          <span>Room temperature</span>{' '}
          <span data-testid="room-controls-current">{formatTemperature(d.current_temperature)}</span>
        </p>
        {target && (
          <>
            <p className="rc-reading">
              <span>Target</span>{' '}
              <span data-testid="room-controls-target">{formatTemperature(target.value)}</span>
            </p>
            <div className="rc-target-set">
              <label htmlFor={`room-controls-target-input-${roomId}`}>New target (°C)</label>
              <input
                id={`room-controls-target-input-${roomId}`}
                type="number"
                data-testid="room-controls-target-input"
                min={bounds.min}
                max={bounds.max}
                step={bounds.step ?? 'any'}
                value={targetInput}
                disabled={busy}
                onChange={(e) => setTargetInput(e.target.value)}
              />
              <button
                type="button"
                data-testid="room-controls-target-set"
                disabled={busy}
                onClick={onSetTarget}
              >
                {inFlight?.control === 'target_temperature' ? 'Sending…' : 'Set target'}
              </button>
            </div>
            {targetError && <p className="rc-target-error" role="alert">{targetError}</p>}
            {targetOutcome && (
              <p role={targetOutcome.role} data-testid="room-controls-target-outcome">{targetOutcome.text}</p>
            )}
          </>
        )}
      </div>

      {showSensors && (
      <div className="rc-section">
        <h4>Sensors</h4>
        {(d.sensors || []).length === 0 ? (
          <p className="rc-muted">No sensors reported.</p>
        ) : (
          <ul className="rc-sensors" data-testid="room-controls-sensors">
            {d.sensors.map((sensor, index) => (
              <li key={`${sensor.name}-${index}`}>
                <span>{sensor.name}</span>{' '}
                <span>{sensorValueText(sensor)}</span>
                {sensor.quality && sensor.quality !== 'fresh' && (
                  <span className="rc-quality"> {QUALITY_WORDS[sensor.quality] || sensor.quality}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}
    </>,
  );
};

export default RoomControlsPanel;
